/**
 * parent/children/$studentId.tsx — Per-child read-only dashboard (N-17).
 *
 * Renders the same widget cluster as the student dashboard
 * (`/me`), but with a "Dilihat oleh orang tua" banner so the parent
 * understands they're viewing their child's progress, not their own.
 * All N-05 student components are reused without modification —
 * they're already read-only surfaces.
 *
 * Access is gated by `parent` role + ownership check
 * (requireApprovedLink) at the API layer; the route just reads the
 * data and renders it.
 */

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { ProgressHeader } from '../../../components/student/ProgressHeader.js';
import { GameProgressCard } from '../../../components/student/GameProgressCard.js';
import { WorksheetProgressCard } from '../../../components/student/WorksheetProgressCard.js';
import { RecentActivityFeed } from '../../../components/student/RecentActivityFeed.js';
import {
  childProgressQuery,
  freshSessionQuery,
  myChildrenQuery,
  type ProgressSummary,
} from '../../../lib/api/queries.js';

export const Route = createFileRoute('/parent/children/$studentId')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role !== 'parent' && user.role !== 'admin') {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: ParentChildDashboard,
});

function ParentChildDashboard(): ReactNode {
  const { studentId } = Route.useParams();
  const progress = useQuery(childProgressQuery(studentId));
  const children = useQuery(myChildrenQuery);

  const childMeta = children.data?.ok
    ? children.data.data.children.find((c) => c.studentId === studentId)
    : null;
  const childName = childMeta?.name ?? 'Anak Anda';

  if (progress.isLoading) {
    return (
      <main className="page-shell">
        <p>Memuat progres…</p>
      </main>
    );
  }

  if (!progress.data?.ok) {
    return (
      <main className="page-shell">
        <PageHeader
          eyebrow="Progres anak"
          title={childName}
          description="Anda tidak memiliki akses untuk melihat data siswa ini."
        />
        <StatePanel
          variant="error"
          title="Akses tidak tersedia"
          description="Hubungi admin sekolah jika Anda merasa ini keliru."
          action={
            <Link to="/parent" className="button button-secondary">
              Kembali ke daftar anak
            </Link>
          }
        />
      </main>
    );
  }

  const data: ProgressSummary = progress.data.data;
  const aggregate = data.aggregate;
  const games = data.games;
  const worksheets = data.worksheets;
  const recent = data.recentActivity;

  return (
    <main
      className="page-shell"
      data-testid="parent-child-dashboard"
      data-view-mode="parent"
    >
      <div
        className="parent-view-banner"
        role="status"
        data-testid="parent-view-banner"
      >
        <span className="parent-view-banner-icon" aria-hidden="true">
          👀
        </span>
        <span>
          Dilihat oleh orang tua. Anda sedang melihat progres belajar{' '}
          <strong>{childName}</strong>.
        </span>
      </div>

      <PageHeader
        eyebrow="Progres anak"
        title={childName}
        description={`Ringkasan progres belajar. ${
          childMeta?.className ? `Kelas: ${childMeta.className}.` : ''
        }`}
        action={
          <Link to="/parent" className="button button-secondary">
            ← Kembali ke daftar anak
          </Link>
        }
      />

      <ProgressHeader studentName={childName} aggregate={aggregate} />

      <section className="section">
        <h2 className="section-title">Game yang dimainkan</h2>
        {games.length === 0 ? (
          <StatePanel
            variant="empty"
            title="Belum ada game yang dimainkan"
            description={`${childName} belum memainkan game apapun.`}
          />
        ) : (
          <div className="card-grid">
            {games.map((g) => (
              <GameProgressCard key={g.gameId} entry={g} />
            ))}
          </div>
        )}
      </section>

      <section className="section">
        <h2 className="section-title">Worksheet yang dikerjakan</h2>
        {worksheets.length === 0 ? (
          <StatePanel
            variant="empty"
            title="Belum ada worksheet"
            description={`${childName} belum mengerjakan worksheet apapun.`}
          />
        ) : (
          <div className="card-grid">
            {worksheets.map((w) => (
              <WorksheetProgressCard key={w.worksheetId} entry={w} />
            ))}
          </div>
        )}
      </section>

      <section className="section">
        <h2 className="section-title">Aktivitas terbaru</h2>
        <RecentActivityFeed events={recent} />
      </section>
    </main>
  );
}